import { useMutation, useQuery } from '@tanstack/react-query';

import { CreateJobApplicationReportParams } from '@restworld/utility-types';
import { useWorkerJobPositionsService } from '@restworld/data-services';
import { QUERY_KEYS } from '@restworld/utils-common';

export function useReportReasons(jobApplicationId: string | undefined, enabled: boolean) {
  const workerJobPositionsService = useWorkerJobPositionsService();

  return useQuery({
    queryKey: [QUERY_KEYS.WORKER_JOB_POSITIONS.REPORT_REASONS, jobApplicationId],
    queryFn: async () => {
      if (!jobApplicationId) return [];
      const { data } = await workerJobPositionsService.getReportReasonsForJobApplication(jobApplicationId);
      return data;
    },
    enabled: !!jobApplicationId && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** The report already sent for this job application, or `null`. Loaded only while the "Report" link can show. */
export function useJobApplicationReport(jobApplicationId: string | undefined, enabled: boolean) {
  const workerJobPositionsService = useWorkerJobPositionsService();

  return useQuery({
    queryKey: [QUERY_KEYS.WORKER_JOB_POSITIONS.JOB_APPLICATION_REPORT, jobApplicationId],
    queryFn: async () => {
      if (!jobApplicationId) return null;
      const { data } = await workerJobPositionsService.getJobApplicationReport(jobApplicationId);
      return data ?? null;
    },
    enabled: !!jobApplicationId && enabled,
  });
}

/** Invalidating `JOB_APPLICATION_REPORT` for the job application is up to the caller, in `onSuccess`. */
export function useCreateJobApplicationReport() {
  const workerJobPositionsService = useWorkerJobPositionsService();

  return useMutation({
    mutationFn: ({
      jobApplicationId,
      params,
    }: {
      jobApplicationId: string;
      params: CreateJobApplicationReportParams;
    }) => workerJobPositionsService.createJobApplicationReport(jobApplicationId, params),
  });
}
